import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { useErrorContext } from '@qpr/client-library';
import { ErrorObject } from '@qpr/client-library/dist/esm/ErrorContext';
import axios, { AxiosError, AxiosResponse } from 'axios';
import React, { useContext } from 'react';
import { useQuery } from 'react-query';
import AppBarContext from '../../../components/BusinessPortalAppBarContext';
import { useDashboard } from '../../Dashboard/DashboardContext';
import DescriptionCard from './DescriptionCard';

const PREFIX = 'KPIDescriptionCard';

const classes = {
  details: `${PREFIX}-details`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.details}`]: {
    position: 'relative',
    width: '100%',
    padding: theme.spacing(1)
  }
}));

export interface KPIDescriptionCardItem {
  title?: string;
  contentText?: string;
  descriptionSeries?: string;
}

interface Data {
  id: string;
  name: string;
  value?: string;
}

export default function KPIDescriptionCard({
  title = 'Description',
  contentText,
  descriptionSeries = 'DESCRIPTION'
}: KPIDescriptionCardItem) {
  const [queryString] = useDashboard();
  const { setError } = useErrorContext();
  const { selectedPeriod, getPeriodName } = useContext(AppBarContext);

  const { isLoading, error, data, isFetched } = useQuery<
    AxiosResponse<Data[]>,
    AxiosError<ErrorObject>
  >(
    `descriptioncard-${queryString.get('meaId')}-${getPeriodName()}-${descriptionSeries}`,
    async () => {
      const response = await axios.post('meaquery', {
        query: `[${queryString.get('meaId')}]`,
        attributes: `name, value(series="${descriptionSeries}", period="${getPeriodName()}")`
      });
      return response.data;
    },
    {
      // The query will not execute until the selectedPeriod exists
      enabled: !!selectedPeriod
    }
  );

  if (error && error.response) setError(error.response.data);

  if (!isLoading && !isFetched) return null;

  if (isLoading)
    return (
      <Root style={{ textAlign: 'center', marginTop: '32px' }}>
        <CircularProgress />
      </Root>
    );

  return data ? (
    <DescriptionCard title={title} contentText={data.data[0].value || contentText} />
  ) : (
    <div className={classes.details} data-testid="KPIDescriptionCard-no-data">
      No data found
    </div>
  );
}
